import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import api from '../../../api/axios';
import useAuthStore from '../../../store/authStore';
import { fmtDate, fmtDateTime, todayAz, todayISO } from '../../../utils/dateFormat';
import SearchSelect from '../../../components/SearchSelect';
import { confirm } from '../../../components/ConfirmDialog';
import PurchaseQaimeModal from '../../../components/PurchaseQaimeModal';

const fmt   = (v) => Number(v || 0).toFixed(2);
const today = todayISO;

// ─── SUPPLIERS TAB ────────────────────────────────────────────────────────────
function SuppliersTab({ isAdmin }) {
    const [suppliers, setSuppliers] = useState([]);
    const [supSearch, setSupSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState({ name: '', phone: '', note: '', pay_from_ofis: false });
    const [saving, setSaving] = useState(false);
    const [paySupplier, setPaySupplier] = useState(null);
    const [payAmount, setPayAmount] = useState('');
    const [payNote, setPayNote] = useState('');
    const [payDate, setPayDate] = useState(today());
    const [paying, setPaying] = useState(false);
    // Borc düzəlişi (əl ilə +/−)
    const [adjSupplier, setAdjSupplier] = useState(null);
    const [adjAmount, setAdjAmount] = useState('');
    const [adjDir, setAdjDir] = useState('decrease'); // 'increase' | 'decrease'
    const [adjNote, setAdjNote] = useState('');
    const [adjusting, setAdjusting] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const res = await api.get('/suppliers'); setSuppliers(res.data.data || []); }
        catch { toast.error('Yüklənmədi.'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditItem(null); setForm({ name: '', phone: '', note: '', pay_from_ofis: false }); setShowForm(true); };
    const openEdit = (s) => { setEditItem(s); setForm({ name: s.name, phone: s.phone || '', note: s.note || '', pay_from_ofis: !!Number(s.pay_from_ofis) }); setShowForm(true); };

    const save = async () => {
        if (!form.name.trim()) return toast.error('Ad tələb olunur.');
        setSaving(true);
        try {
            if (editItem) await api.put(`/suppliers/${editItem.id}`, form);
            else await api.post('/suppliers', form);
            toast.success(editItem ? 'Yeniləndi.' : 'Əlavə edildi.');
            setShowForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    const payDebt = async () => {
        if (!payAmount || Number(payAmount) <= 0) return toast.error('Məbləğ daxil edin.');
        setPaying(true);
        try {
            await api.post(`/suppliers/${paySupplier.id}/pay`, { amount: payAmount, note: payNote });
            toast.success('Ödəniş qeyd edildi.'); setPaySupplier(null); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setPaying(false); }
    };

    const doAdjust = async () => {
        if (!adjAmount || Number(adjAmount) <= 0) return toast.error('Məbləğ daxil edin.');
        setAdjusting(true);
        try {
            await api.post(`/suppliers/${adjSupplier.id}/adjust`, { amount: adjAmount, direction: adjDir, note: adjNote });
            toast.success(adjDir === 'decrease' ? 'Borc azaldıldı.' : 'Borc artırıldı.');
            setAdjSupplier(null); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setAdjusting(false); }
    };

    const doDelete = async (s) => {
        const ok = await confirm({
            title: 'Tədarükçünü sil',
            message: `"${s.name}" silinsin? Borc: ${fmt(s.debt)} ₼. (Alış/ödəniş tarixçəsi qalır, siyahıdan çıxarılır.)`,
            confirmText: 'Sil', danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/suppliers/${s.id}`);
            toast.success('Silindi.'); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    const q = supSearch.trim().toLowerCase();
    const filteredSuppliers = q
        ? suppliers.filter(s => s.name.toLowerCase().includes(q) || (s.phone || '').toLowerCase().includes(q))
        : suppliers;

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <input
                    type="text"
                    value={supSearch}
                    onChange={e => setSupSearch(e.target.value)}
                    placeholder="🔍 Tədarükçü axtar..."
                    className="flex-1 min-w-[200px] border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-blue-400"
                />
                <p className="text-sm text-slate-500 whitespace-nowrap">{filteredSuppliers.length} / {suppliers.length} tədarükçü</p>
                {isAdmin && (
                    <button onClick={openAdd}
                        className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 whitespace-nowrap">
                        + Əlavə et
                    </button>
                )}
            </div>

            {loading ? <p className="text-center text-slate-400 py-10">Yüklənir...</p> : (
                <div className="grid gap-3">
                    {filteredSuppliers.map(s => (
                        <div key={s.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex-1">
                                    <p className="font-bold text-slate-800">{s.name}</p>
                                    {s.phone && <p className="text-sm text-slate-400">📞 {s.phone}</p>}
                                    <div className="flex gap-4 mt-2 text-sm">
                                        <span>Borc: <b className={Number(s.debt) > 0 ? 'text-red-600' : 'text-green-600'}>{fmt(s.debt)} ₼</b></span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 shrink-0 justify-end">
                                    {Number(s.debt) > 0 && (
                                        <button onClick={() => { setPaySupplier(s); setPayAmount(''); setPayNote(''); setPayDate(today()); }}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 font-semibold">
                                            💵 Borc öd
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button onClick={() => { setAdjSupplier(s); setAdjAmount(''); setAdjDir('decrease'); setAdjNote(''); }}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold">
                                            ⚖️ Borc düzəlt
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button onClick={() => openEdit(s)}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium">
                                            Düzəlt
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button onClick={() => doDelete(s)}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 font-semibold">
                                            🗑 Sil
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {!filteredSuppliers.length && <p className="text-center text-slate-400 py-10">{suppliers.length ? 'Axtarışa uyğun tədarükçü yoxdur.' : 'Hələ tədarükçü yoxdur.'}</p>}
                </div>
            )}

            {/* Supplier form */}
            {showForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4">{editItem ? 'Düzəlt' : 'Yeni tədarükçü'}</h2>
                        <div className="grid gap-3">
                            <FF label="Ad" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
                            <FF label="Telefon" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} />
                            <FF label="Qeyd" value={form.note} onChange={v => setForm(f => ({ ...f, note: v }))} />
                            <label className="flex items-center gap-2 cursor-pointer p-3 rounded-xl bg-amber-50 border border-amber-200">
                                <input
                                    type="checkbox"
                                    checked={form.pay_from_ofis}
                                    onChange={e => setForm(f => ({ ...f, pay_from_ofis: e.target.checked }))}
                                    className="w-4 h-4 accent-amber-600"
                                />
                                <span className="text-sm font-semibold text-amber-800">Ofisə ödənilsin</span>
                            </label>
                            {form.pay_from_ofis && (
                                <p className="text-xs text-amber-700 -mt-2 px-1">
                                    💡 Bu tədarükçüyə borc ödəniləndə pul əvvəlcə kassaya yazılır,
                                    sonra avtomatik ofisdən balanslaşdırılır.
                                </p>
                            )}
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setShowForm(false)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={save} disabled={saving}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
                                {saving ? '...' : 'Saxla'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pay debt modal */}
            {paySupplier && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-1">Borc ödə — {paySupplier.name}</h2>
                        <p className="text-sm text-slate-500 mb-4">Cari borc: <b className="text-red-600">{fmt(paySupplier.debt)} ₼</b></p>
                        <div className="grid gap-3">
                            <FF label="Məbləğ (₼)" type="number" value={payAmount} onChange={v => setPayAmount(v)} />
                            <FF label="Qeyd" value={payNote} onChange={v => setPayNote(v)} />
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setPaySupplier(null)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={payDebt} disabled={paying}
                                className="flex-1 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-50">
                                {paying ? '...' : 'Ödə'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Borc düzəliş modalı — əl ilə +/− (kassaya toxunmur) */}
            {adjSupplier && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-1">Borc düzəlişi — {adjSupplier.name}</h2>
                        <p className="text-sm text-slate-500 mb-4">Cari borc: <b className={Number(adjSupplier.debt) > 0 ? 'text-red-600' : 'text-green-600'}>{fmt(adjSupplier.debt)} ₼</b></p>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <button onClick={() => setAdjDir('decrease')}
                                className={`py-2 rounded-xl text-sm font-semibold border ${adjDir === 'decrease' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                                − Borcu azalt
                            </button>
                            <button onClick={() => setAdjDir('increase')}
                                className={`py-2 rounded-xl text-sm font-semibold border ${adjDir === 'increase' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                                + Borcu artır
                            </button>
                        </div>
                        <div className="grid gap-3">
                            <FF label="Məbləğ (₼)" type="number" value={adjAmount} onChange={v => setAdjAmount(v)} />
                            <FF label="Qeyd (səbəb)" value={adjNote} onChange={v => setAdjNote(v)} />
                        </div>
                        <p className="text-xs text-slate-400 mt-2 px-1">
                            ℹ️ Bu düzəliş yalnız tədarükçü borcunu dəyişir, kassaya təsir etmir.
                        </p>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setAdjSupplier(null)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={doAdjust} disabled={adjusting}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
                                {adjusting ? '...' : 'Təsdiqlə'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── PURCHASES (INGREDIENTS) TAB ──────────────────────────────────────────────
function PurchasesTab({ isAdmin }) {
    // ── data ──
    const [purchases,   setPurchases]   = useState([]);
    const [suppliers,   setSuppliers]   = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [loading,     setLoading]     = useState(true);

    // ── current qaime session ──
    const [nextNo,      setNextNo]      = useState(null);
    const [supplierId,  setSupplierId]  = useState('');
    const [items,       setItems]       = useState([]);   // confirmed right-panel rows

    // ── left-panel single-item inputs ──
    const [search,       setSearch]       = useState('');
    const [showDrop,     setShowDrop]     = useState(false);
    const [selectedIng,  setSelectedIng]  = useState(null);
    const [qty,          setQty]          = useState('');
    const [totalPrice,   setTotalPrice]   = useState('');

    // ── misc ──
    const [saving,       setSaving]       = useState(false);
    const [viewPurchase, setViewPurchase] = useState(null);

    // Tarix filtri + qaimə № axtarışı
    const [fromDate, setFromDate] = useState(today());
    const [toDate,   setToDate]   = useState(today());
    const [qaimeNo,  setQaimeNo]  = useState('');

    const supplierLocked = items.length > 0;
    const filteredIng    = ingredients.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase())
    );
    const unitPrice = (qty && totalPrice && Number(qty) > 0)
        ? (Number(totalPrice) / Number(qty)).toFixed(4)
        : '';
    const grandTotal = items.reduce((s, it) => s + it.total_price, 0);
    const todayDisplay = todayAz();

    const load = async (fromD = fromDate, toD = toDate) => {
        setLoading(true);
        try {
            const [pRes, sRes, iRes, noRes] = await Promise.all([
                api.get('/purchases', { params: { from: fromD, to: toD } }),
                api.get('/suppliers'),
                api.get('/ingredients'),
                api.get('/purchases/next-no'),
            ]);
            setPurchases(pRes.data.data   || []);
            setSuppliers(sRes.data.data   || []);
            setIngredients(iRes.data.data || []);
            setNextNo(noRes.data.next_no);
        } catch { toast.error('Yüklənmədi.'); }
        finally  { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // close dropdown on outside click
    useEffect(() => {
        const handler = () => setShowDrop(false);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    const addItem = () => {
        if (!selectedIng)                        return toast.error('Məhsul seçin.');
        if (!qty || Number(qty) <= 0)            return toast.error('Miqdar daxil edin.');
        if (!totalPrice || Number(totalPrice) <= 0) return toast.error('Cəmi daxil edin.');
        const up = Number(qty) > 0 ? Number(totalPrice) / Number(qty) : 0;
        setItems(prev => [...prev, {
            ingredient_id: selectedIng.id,
            name:          selectedIng.name,
            unit:          selectedIng.unit,
            qty:           Number(qty),
            total_price:   Number(totalPrice),
            unit_price:    up,
        }]);
        setSelectedIng(null); setSearch(''); setQty(''); setTotalPrice('');
    };

    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    const cancel = () => {
        setItems([]); setSupplierId('');
        setSelectedIng(null); setSearch(''); setQty(''); setTotalPrice('');
    };

    const savePurchase = async () => {
        if (!items.length) return toast.error('Heç bir məhsul əlavə edilməyib.');
        setSaving(true);
        try {
            await api.post('/purchases', {
                supplier_id: supplierId || null,
                date: today(),
                items: items.map(it => ({
                    ingredient_id: it.ingredient_id,
                    qty:           it.qty,
                    unit:          it.unit,
                    unit_price:    it.unit_price,
                    total_price:   it.total_price,
                })),
            });
            toast.success('Bazarlıq qeyd edildi.');
            cancel();
            setFromDate(today()); setToDate(today());
            load(today(), today());
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    const openView = async (p) => {
        try { const res = await api.get(`/purchases/${p.id}`); setViewPurchase(res.data.data); }
        catch { toast.error('Yüklənmədi.'); }
    };

    // Qaimə nömrəsi ilə tap → view modalında aç (baxıb geri qaytarmaq üçün)
    const searchQaime = async () => {
        const no = String(qaimeNo).trim();
        if (!no) return toast.error('Qaimə nömrəsi yazın.');
        try { const res = await api.get(`/purchases/${no}`); setViewPurchase(res.data.data); }
        catch (err) { toast.error(err.response?.data?.msg || 'Qaimə tapılmadı.'); }
    };

    const deletePurchase = async (p) => {
        if (!(await confirm({
            title: 'Qaiməni sil',
            message: `Qaime #${p.id} silinsin? Stok geri qaytarılacaq (−miqdar), tədarükçü borcu silinər.`,
            confirmText: 'Sil', danger: true,
        }))) return;
        try { await api.delete(`/purchases/${p.id}`); toast.success('Silindi, stok geri qaytarıldı.'); load(); }
        catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    const currentSupplier = suppliers.find(s => Number(s.id) === Number(supplierId));

    return (
        <div>
            {/* ═══ WORKSPACE: left form + right live table (mobil: stack, desktop: yan-yana) ═══ */}
            <div className="flex flex-col lg:flex-row gap-4 mb-5 lg:min-h-[420px]">

                {/* ── LEFT PANEL ── */}
                <div className="w-full lg:w-72 lg:shrink-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3">

                    {/* Qaime № + Tarix */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Qaime №</p>
                            <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm font-bold text-slate-700 text-center">
                                #{nextNo ?? '…'}
                            </div>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Tarix</p>
                            <div className="bg-slate-100 rounded-lg px-3 py-2 text-xs text-slate-600 font-medium text-center">
                                {todayDisplay}
                            </div>
                        </div>
                    </div>

                    {/* Supplier */}
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Tədarükçü</p>
                        <SearchSelect
                            value={supplierId}
                            onChange={setSupplierId}
                            options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                            placeholder="Seç (ixtiyari)"
                            disabled={supplierLocked}
                        />
                        {supplierLocked && (
                            <p className="text-[10px] text-amber-500 mt-0.5">🔒 Qaime başlandı, dəyişdirilmir</p>
                        )}
                    </div>

                    <hr className="border-slate-100" />

                    {/* Ingredient search */}
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Məhsul</p>
                        {selectedIng ? (
                            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                <span className="text-sm font-semibold text-blue-800 flex-1 truncate">{selectedIng.name}</span>
                                <span className="text-xs text-blue-500 shrink-0">{selectedIng.unit}</span>
                                <button
                                    onClick={() => { setSelectedIng(null); setSearch(''); }}
                                    className="text-blue-400 hover:text-blue-600 text-lg leading-none ml-1">×</button>
                            </div>
                        ) : (
                            <div className="relative" onClick={e => e.stopPropagation()}>
                                <input
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setShowDrop(true); }}
                                    onFocus={() => setShowDrop(true)}
                                    placeholder="Axtar..."
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                                />
                                {showDrop && (search || true) && filteredIng.length > 0 && (
                                    <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                                        {filteredIng.map(i => (
                                            <button key={i.id}
                                                onClick={() => { setSelectedIng(i); setSearch(''); setShowDrop(false); }}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between gap-2 border-b border-slate-50 last:border-0">
                                                <span className="truncate">{i.name}</span>
                                                <span className="text-xs text-slate-400 shrink-0">{i.unit}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {showDrop && search && filteredIng.length === 0 && (
                                    <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2">
                                        <p className="text-xs text-slate-400">Tapılmadı</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Qty + Total */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">
                                Miqdar{selectedIng ? ` (${selectedIng.unit})` : ''}
                            </p>
                            <input
                                type="number" min="0"
                                step={selectedIng?.unit === 'kq' ? '0.001' : '1'}
                                value={qty}
                                onChange={e => setQty(e.target.value)}
                                placeholder="0"
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                            />
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Cəmi (₼)</p>
                            <input
                                type="number" min="0" step="0.01"
                                value={totalPrice}
                                onChange={e => setTotalPrice(e.target.value)}
                                placeholder="0.00"
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                            />
                        </div>
                    </div>

                    {/* Unit price display */}
                    <div className={`rounded-lg px-3 py-1.5 text-xs transition-colors ${unitPrice ? 'bg-blue-50 text-slate-600' : 'bg-slate-50 text-slate-300'}`}>
                        Vahid qiymət:{' '}
                        <span className="font-bold">
                            {unitPrice ? `${Number(unitPrice).toFixed(2)} ₼ / ${selectedIng?.unit || 'vahid'}` : '—'}
                        </span>
                    </div>

                    {/* Add button */}
                    <button onClick={addItem}
                        className="w-full py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">
                        + Əlavə et
                    </button>

                    <hr className="border-slate-100" />

                    {/* Grand total */}
                    <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-2.5 text-center">
                        <p className="text-[10px] text-slate-500 mb-0.5 uppercase tracking-wide">Qaime cəmi</p>
                        <p className="text-2xl font-bold text-green-700">{fmt(grandTotal)} ₼</p>
                    </div>

                    {/* Confirm / Cancel */}
                    <div className="grid grid-cols-2 gap-2">
                        <button onClick={cancel}
                            className="py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
                            Ləğv et
                        </button>
                        <button onClick={savePurchase} disabled={saving || !items.length}
                            className="py-2 rounded-xl bg-green-600 text-white text-sm font-bold hover:bg-green-700 disabled:opacity-40 transition-colors">
                            {saving ? '...' : '✓ Təsdiqle'}
                        </button>
                    </div>
                </div>

                {/* ── RIGHT PANEL: live qaime table (mobildə görünən hündürlük) ── */}
                <div className="flex-1 min-w-0 flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[380px] lg:min-h-0">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 shrink-0">
                        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                            Cari Qaime — #{nextNo ?? '…'}
                            {currentSupplier && (
                                <span className="ml-2 font-normal normal-case text-blue-600">@ {currentSupplier.name}</span>
                            )}
                        </p>
                    </div>

                    {items.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-300 gap-2 py-16">
                            <span className="text-4xl">🛒</span>
                            <p className="text-sm">Soldan məhsul seçib əlavə edin</p>
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto">
                            <div className="overflow-x-auto"><table className="w-full text-sm min-w-[700px]">
                                <thead className="bg-slate-50 sticky top-0 shadow-sm">
                                    <tr className="text-xs text-slate-500">
                                        <th className="text-left px-4 py-2.5">#</th>
                                        <th className="text-left px-3 py-2.5">Məhsul</th>
                                        <th className="text-right px-3 py-2.5">Miqdar</th>
                                        <th className="text-right px-3 py-2.5">Vahid ₼</th>
                                        <th className="text-right px-3 py-2.5">Cəmi ₼</th>
                                        <th className="w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it, i) => (
                                        <tr key={i} className={`border-t border-slate-100 ${i % 2 === 1 ? 'bg-slate-50/60' : ''}`}>
                                            <td className="px-4 py-3 text-xs text-slate-400 font-medium">{i + 1}</td>
                                            <td className="px-3 py-3 font-semibold text-slate-800">{it.name}</td>
                                            <td className="px-3 py-3 text-right text-slate-600">{it.qty} {it.unit}</td>
                                            <td className="px-3 py-3 text-right text-slate-500 text-xs">{fmt(it.unit_price)}</td>
                                            <td className="px-3 py-3 text-right font-bold text-slate-800">{fmt(it.total_price)}</td>
                                            <td className="px-2 py-3 text-center">
                                                <button onClick={() => removeItem(i)}
                                                    className="text-red-400 hover:text-red-600 text-xl leading-none w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 mx-auto">×</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="border-t-2 border-slate-200">
                                    <tr className="bg-green-50">
                                        <td colSpan="4" className="px-4 py-3 text-right font-bold text-slate-600">Cəmi:</td>
                                        <td className="px-3 py-3 text-right font-bold text-green-700 text-base">{fmt(grandTotal)} ₼</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table></div>
                        </div>
                    )}
                </div>
            </div>

            {/* ═══ FİLTR: tarix + qaimə № ═══ */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-end gap-3">
                <div>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">Tarixdən</label>
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">Tarixə</label>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <button onClick={() => load(fromDate, toDate)}
                    className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold">Göstər</button>
                <button onClick={() => { setFromDate(today()); setToDate(today()); load(today(), today()); }}
                    className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-medium">Bugün</button>

                <div className="flex-1 min-w-[12px]" />

                <div>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">Qaimə № ilə tap</label>
                    <div className="flex gap-2">
                        <input type="number" value={qaimeNo} onChange={e => setQaimeNo(e.target.value)}
                            placeholder="məs. 7093"
                            onKeyDown={e => { if (e.key === 'Enter') searchQaime(); }}
                            className="w-28 border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400" />
                        <button onClick={searchQaime}
                            className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-bold whitespace-nowrap">🔍 Tap</button>
                    </div>
                </div>
            </div>

            {/* ═══ PAST QAIMES LIST ═══ */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Qaimeler ({fmtDate(fromDate)}{toDate !== fromDate ? ` – ${fmtDate(toDate)}` : ''})</p>
                    <p className="text-xs text-slate-400">{purchases.length} qaime</p>
                </div>

                {loading ? (
                    <p className="text-center text-slate-400 py-8 text-sm">Yüklənir...</p>
                ) : !purchases.length ? (
                    <p className="text-center text-slate-400 py-8 text-sm">Hələ bazarlıq yoxdur.</p>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {purchases.map(p => (
                            <div key={p.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-slate-800 text-sm">#{p.id}</span>
                                        <span className="text-xs text-slate-400">{fmtDateTime(p.created_at)}</span>
                                        {p.supplier_name && (
                                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{p.supplier_name}</span>
                                        )}
                                    </div>
                                    <p className="text-sm font-semibold text-green-700 mt-0.5">{fmt(p.total)} ₼</p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button onClick={() => openView(p)}
                                        className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium">Bax</button>
                                    {isAdmin && (
                                        <button onClick={() => deletePurchase(p)}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 font-medium">Sil</button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ═══ VIEW QAIME MODAL — ortaq komponent (Hesabatlarda da eynisi) ═══ */}
            {viewPurchase && (
                <PurchaseQaimeModal purchaseId={viewPurchase.id} isAdmin={isAdmin}
                    onClose={() => setViewPurchase(null)}
                    onChanged={load} />
            )}
        </div>
    );
}

// ─── DIGER (NON-REVENUE) TAB ──────────────────────────────────────────────────
function DigerTab({ isAdmin }) {
    const [categories, setCategories] = useState([]);
    const [otherExpenses, setOtherExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCatForm, setShowCatForm] = useState(false);
    const [editCat, setEditCat] = useState(null);
    const [catName, setCatName] = useState('');
    const [showEntryForm, setShowEntryForm] = useState(false);
    const [entryForm, setEntryForm] = useState({ ref_id: '', amount: '', note: '' });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [catRes, oeRes] = await Promise.all([
                api.get('/other-expenses/categories'),
                api.get('/other-expenses'),
            ]);
            setCategories(catRes.data.data || []);
            setOtherExpenses(oeRes.data.data || []);
        } catch { toast.error('Yüklənmədi.'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const saveCat = async () => {
        if (!catName.trim()) return toast.error('Ad tələb olunur.');
        try {
            if (editCat) await api.put(`/other-expenses/categories/${editCat.id}`, { name: catName });
            else await api.post('/other-expenses/categories', { name: catName });
            toast.success('Saxlandı.'); setShowCatForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    const deleteCat = async (c) => {
        if (!(await confirm(`"${c.name}" silinsin?`))) return;
        try { await api.delete(`/other-expenses/categories/${c.id}`); toast.success('Silindi.'); load(); }
        catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    const saveEntry = async () => {
        if (!entryForm.amount || Number(entryForm.amount) <= 0) return toast.error('Məbləğ daxil edin.');
        setSaving(true);
        try {
            await api.post('/other-expenses', { category_id: entryForm.ref_id || null, amount: entryForm.amount, note: entryForm.note });
            toast.success('Qeyd edildi.'); setShowEntryForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setSaving(false); }
    };

    const deleteEntry = async (id) => {
        if (!(await confirm('Silinsin?'))) return;
        try { await api.delete(`/other-expenses/${id}`); toast.success('Silindi.'); load(); }
        catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
    };

    const total = otherExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);

    return (
        <div>
            {/* Categories — collapsed by default */}
            <details className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 group">
                <summary className="cursor-pointer flex items-center justify-between p-4 list-none">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-700">📂 Kateqoriyalar</span>
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{categories.length}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {isAdmin && (
                            <button onClick={e => { e.preventDefault(); e.stopPropagation(); setEditCat(null); setCatName(''); setShowCatForm(true); }}
                                className="text-xs text-blue-600 hover:text-blue-800 font-semibold">+ Əlavə et</button>
                        )}
                        <span className="text-slate-400 text-xs group-open:rotate-180 transition-transform">▼</span>
                    </div>
                </summary>
                <div className="px-4 pb-4 max-h-64 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                        {categories.map(item => (
                            <div key={item.id} className="flex items-center gap-1 bg-slate-100 rounded-full px-3 py-1">
                                <span className="text-xs text-slate-700">{item.name}</span>
                                {isAdmin && <>
                                    <button onClick={() => { setEditCat(item); setCatName(item.name); setShowCatForm(true); }}
                                        className="text-blue-400 hover:text-blue-600 text-xs ml-1">✎</button>
                                    <button onClick={() => deleteCat(item)} className="text-red-300 hover:text-red-500 text-xs">×</button>
                                </>}
                            </div>
                        ))}
                        {!categories.length && (
                            <p className="text-xs text-slate-400">
                                {isAdmin ? 'Kateqoriya yoxdur. Əlavə edin.' : 'Kateqoriya yoxdur.'}
                            </p>
                        )}
                    </div>
                </div>
            </details>

            <div className="flex items-center justify-between mb-3">
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-2 flex gap-4 text-sm">
                    <span className="text-slate-600">Cəmi:</span>
                    <span className="font-bold text-amber-700">{fmt(total)} ₼</span>
                </div>
                <button onClick={() => setShowEntryForm(true)}
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                    + Yeni qeyd
                </button>
            </div>

            {loading ? <p className="text-center text-slate-400 py-10">Yüklənir...</p> : (
                <div className="grid gap-2">
                    {otherExpenses.map(e => (
                        <div key={e.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 flex items-center gap-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-slate-800">{fmt(e.amount)} ₼</span>
                                    {e.category_name && <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{e.category_name}</span>}
                                    <span className="text-xs text-slate-400">{fmtDateTime(e.created_at)}</span>
                                </div>
                                {e.note && <p className="text-xs text-slate-400 mt-0.5">📝 {e.note}</p>}
                            </div>
                            {isAdmin && (
                                <button onClick={() => deleteEntry(e.id)}
                                    className="text-xs px-2 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-500">Sil</button>
                            )}
                        </div>
                    ))}
                    {!otherExpenses.length && <p className="text-center text-slate-400 py-10">Qeyd yoxdur.</p>}
                </div>
            )}

            {/* Category modal */}
            {showCatForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4">{editCat ? 'Kateqoriyanı düzəlt' : 'Yeni kateqoriya'}</h2>
                        <input value={catName} onChange={e => setCatName(e.target.value)} placeholder="Kateqoriya adı"
                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setShowCatForm(false)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={saveCat}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold">Saxla</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Entry modal */}
            {showEntryForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4">Digər bazarlıq qeyd et</h2>
                        <div className="grid gap-3">
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Kateqoriya</label>
                                <SearchSelect
                                    value={entryForm.ref_id}
                                    onChange={v => setEntryForm(f => ({ ...f, ref_id: v }))}
                                    options={categories.map(o => ({ value: o.id, label: o.name }))}
                                    placeholder="Seç (ixtiyari)"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Məbləğ (₼)</label>
                                <input type="number" step="0.01" value={entryForm.amount}
                                    onChange={e => setEntryForm(f => ({ ...f, amount: e.target.value }))}
                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Qeyd</label>
                                <input value={entryForm.note}
                                    onChange={e => setEntryForm(f => ({ ...f, note: e.target.value }))}
                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setShowEntryForm(false)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={saveEntry} disabled={saving}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
                                {saving ? '...' : 'Qeyd et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function Bazarliq() {
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get('tab') || 'tedarukculer');

    useEffect(() => {
        const t = searchParams.get('tab');
        if (t) setTab(t);
    }, [searchParams]);

    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

    const tabs = [
        { key: 'tedarukculer', label: '🏢 Tədarükçülər', color: 'blue' },
        { key: 'alislar',      label: '🛒 Bazarlıq',     color: 'green' },
        { key: 'diger',        label: '🧹 Bazarlıq',     color: 'red' },
    ];

    const tabCls = (t) => {
        const active = tab === t.key;
        if (t.color === 'green') return active
            ? 'bg-green-600 text-white'
            : 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100';
        if (t.color === 'red') return active
            ? 'bg-red-500 text-white'
            : 'bg-red-50 text-red-600 border border-red-300 hover:bg-red-100';
        return active
            ? 'bg-blue-600 text-white'
            : 'bg-slate-100 text-slate-600 hover:bg-slate-200';
    };

    return (
        <Layout>
            <div className="p-4 max-w-6xl mx-auto">
                <h1 className="text-xl font-bold text-slate-800 mb-4">Bazarlıq</h1>

                <div className="flex gap-2 mb-5 flex-wrap">
                    {tabs.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tabCls(t)}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === 'tedarukculer' && <SuppliersTab isAdmin={isAdmin} />}
                {tab === 'alislar' && <PurchasesTab isAdmin={isAdmin} />}
                {tab === 'diger' && <DigerTab isAdmin={isAdmin} />}
            </div>
        </Layout>
    );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const FF = ({ label, value, onChange, type = 'text' }) => (
    <div>
        <label className="text-xs text-slate-500 mb-1 block">{label}</label>
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
    </div>
);
